import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { toColorString } from 'polished';
import { t } from '@lingui/macro';
import { Button, H4 } from '@decisiv/ui-components';
import ExclamationCircle from '@decisiv/iconix/lib/components/ExclamationCircle';
import Times from '@decisiv/iconix/lib/components/Times';
import color from '@decisiv/design-tokens/lib/color';

// Shared info affordance for the Global Settings feature sections: an
// ExclamationCircle button next to the section heading that opens one consistent
// right-side drawer with the section's help content. Portal-rendered so no
// parent flex/overflow context can distort it; dismissed via Esc, the close
// button, or clicking the overlay.
const snowWhite = toColorString(color.base.snowWhite);
const halfMoon = toColorString(color.base.halfMoon);
const charcoal = toColorString(color.base.charcoal);
const alaskanHusky = toColorString(color.base.alaskanHusky);
const pacificOcean = toColorString(color.interaction.pacificOcean);

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

const TriggerWrap = styled.span`
  display: inline-flex;
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  justify-content: flex-end;
  background: var(--if-overlay);

  @media (prefers-reduced-motion: no-preference) {
    animation: fid-fade 0.2s ease;
  }

  @keyframes fid-fade {
    from {
      opacity: 0;
    }
  }
`;

const Panel = styled.aside`
  width: 460px;
  max-width: 92vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${snowWhite};
  box-shadow: -12px 0 40px -12px var(--if-shadow);

  @media (prefers-reduced-motion: no-preference) {
    animation: fid-slide 0.24s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @keyframes fid-slide {
    from {
      transform: translateX(100%);
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 20px 22px 14px;
  border-bottom: 1px solid ${halfMoon};
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${charcoal};
`;

const CloseButton = styled.button`
  flex: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${alaskanHusky};
  padding: 4px;
  border-radius: 6px;
  line-height: 0;

  &:hover {
    color: ${charcoal};
  }

  &:focus-visible {
    outline: 2px solid ${pacificOcean};
    outline-offset: 2px;
  }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 22px 28px;
  font-size: 14px;
  line-height: 1.5;
  color: ${charcoal};

  p {
    margin: 0 0 12px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul {
    margin: 0 0 12px;
    padding-left: 20px;
  }

  li {
    margin: 0 0 6px;
  }

  li:last-child {
    margin-bottom: 0;
  }

  h4 {
    font-size: 14.5px;
    font-weight: 600;
    color: ${charcoal};
    margin: 22px 0 8px;
  }

  code {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.85em;
    background: ${halfMoon};
    padding: 1px 5px;
    border-radius: 5px;
    overflow-wrap: anywhere;
  }
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  margin: 6px 0 14px;

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
  }

  th,
  td {
    text-align: left;
    padding: 7px 10px;
    border: 1px solid ${halfMoon};
    vertical-align: top;
  }

  th {
    background: ${toColorString(color.base.fullMoon)};
    font-weight: 600;
    white-space: nowrap;
  }

  /* Keep the key/attribute (first column) on a single line and let the
     column size to fit it, instead of breaking the identifier mid-word. */
  td:first-child {
    white-space: nowrap;
  }

  td:first-child code {
    white-space: nowrap;
    overflow-wrap: normal;
  }

  .caption {
    margin: 4px 0 0;
    font-size: 12px;
    color: ${alaskanHusky};
  }
`;

const Lead = styled.p`
  font-weight: 600;
  color: ${charcoal};
`;

const FeatureInfoDrawer = ({
  heading,
  title = null,
  lead = null,
  ariaLabel = 'More information',
  wide = false,
  children,
}) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const close = () => setOpen(false);

  // While open: move focus into the drawer, keep Tab within it, close on
  // Escape, and restore focus to the trigger when it closes.
  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    const focusable = () =>
      panel
        ? Array.from(
            panel.querySelectorAll(
              'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])',
            ),
          )
        : [];

    (focusable()[0] || panel)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusable();
      if (items.length === 0) return;

      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  return (
    <Wrapper>
      <TriggerWrap>
        <Button
          icon={ExclamationCircle}
          variant="ghost"
          style={{ cursor: 'pointer', marginRight: 5 }}
          aria-label={ariaLabel}
          aria-expanded={open}
          onClick={() => setOpen(true)}
        />
      </TriggerWrap>
      <H4 style={{ marginTop: 6 }}>{heading}</H4>
      {open &&
        createPortal(
          <Overlay
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) close();
            }}
          >
            <Panel
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              tabIndex={-1}
              style={wide ? { width: 640, maxWidth: '94vw' } : undefined}
            >
              <Header>
                <Title>{title || heading}</Title>
                <CloseButton
                  type="button"
                  aria-label={t`Close`}
                  onClick={close}
                >
                  <Times size="small" />
                </CloseButton>
              </Header>
              <Body>
                {lead ? <Lead>{lead}</Lead> : null}
                {children}
              </Body>
            </Panel>
          </Overlay>,
          document.body,
        )}
    </Wrapper>
  );
};

FeatureInfoDrawer.propTypes = {
  heading: PropTypes.node.isRequired,
  title: PropTypes.node,
  lead: PropTypes.node,
  ariaLabel: PropTypes.string,
  wide: PropTypes.bool,
  children: PropTypes.node.isRequired,
};
export default FeatureInfoDrawer;
